import { useEffect, useState } from "react";
import {
  ChevronLeft, Smartphone, BatteryCharging, Camera, Plug, Droplets, Wrench, Clock, Check, Loader2,
  Package, Ear, Volume2, Mic, ScanFace, Power, Vibrate, Database, Code2, Search, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { whatsappLinks } from "@/lib/whatsapp";

const ISSUE_ICONS = {
  display: Smartphone,
  battery: BatteryCharging,
  charging: Plug,
  backcover: Package,
  rear_camera: Camera,
  front_camera: Camera,
  earpiece: Ear,
  speaker: Volume2,
  microphone: Mic,
  faceid: ScanFace,
  power_button: Power,
  volume_button: Volume2,
  vibration: Vibrate,
  water: Droplets,
  datarescue: Database,
  software: Code2,
  diagnostic: Search,
  other: Wrench,
};

// Per-brand model series tabs. Only defined for brands where the list is long
// enough to benefit from grouping. Apple stays as a flat list per user choice.
const MODEL_GROUPS = {
  samsung: [
    { id: "s",     name: "S-Serie",   match: (m) => /^Galaxy S\d/.test(m) && !/Note/i.test(m) },
    { id: "note",  name: "Note",      match: (m) => /Note/i.test(m) },
    { id: "fold",  name: "Fold / Flip", match: (m) => /Fold|Flip/i.test(m) },
    { id: "a",     name: "A-Serie",   match: (m) => /^Galaxy A\d/.test(m) },
  ],
};

const BRAND_LABEL = {
  apple: "Apple", samsung: "SAMSUNG", xiaomi: "Xiaomi", huawei: "HUAWEI",
  google: "Google", honor: "HONOR", other: "+",
};

export default function RepairConfigurator() {
  const [step, setStep] = useState(1);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [issues, setIssues] = useState([]);
  const [sel, setSel] = useState({ brand: null, model: null, issue: null });
  const [quote, setQuote] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);  // {name, price} or null
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", notes: "" });
  const [done, setDone] = useState(false);
  const [modelGroup, setModelGroup] = useState(null); // active tab id within a brand
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/catalog/brands").then((r) => setBrands(r.data));
    api.get("/catalog/issues").then((r) => setIssues(r.data));
  }, []);

  useEffect(() => {
    if (sel.brand) api.get(`/catalog/models/${sel.brand.id}`).then((r) => setModels(r.data));
  }, [sel.brand]);

  // Derive the active model-group tab: user-selected if it matches a group,
  // otherwise the first group that has matching models.
  const activeModelGroup = (() => {
    if (!sel.brand) return null;
    const groups = MODEL_GROUPS[sel.brand.id];
    if (!groups || models.length === 0) return null;
    if (modelGroup && groups.find((g) => g.id === modelGroup)) return modelGroup;
    return groups.find((g) => models.some(g.match))?.id || groups[0].id;
  })();

  useEffect(() => {
    if (sel.brand && sel.model && sel.issue) {
      api.post("/catalog/quote", { brand: sel.brand.id, model: sel.model, issue: sel.issue.id })
        .then((r) => {
          setQuote(r.data);
          // Pre-select cheapest variant if variants exist
          const vs = r.data?.variants || [];
          if (vs.length > 0) {
            const cheapest = vs.reduce((a, b) => (a.price <= b.price ? a : b));
            setSelectedVariant(cheapest);
          } else {
            setSelectedVariant(null);
          }
        });
    }
  }, [sel]);

  const pick = (key, value, nextStep) => {
    setSel((s) => {
      const next = { ...s, [key]: value };
      if (key === "brand") { next.model = null; next.issue = null; }
      if (key === "model") { next.issue = null; }
      return next;
    });
    if (key !== "issue") { setQuote(null); setSelectedVariant(null); }
    if (key === "brand") { setModelGroup(null); setSearch(""); } // reset tab + search
    setTimeout(() => setStep(nextStep), 250);
  };

  // Effective displayed price: variant price if selected, else base quote price
  const effectivePrice = selectedVariant ? selectedVariant.price : (quote?.price ?? 0);
  const hasVariants = (quote?.variants?.length || 0) > 0;

  const submit = async () => {
    if (!form.customer_name || !form.phone) { toast.error("Bitte Name und Telefon eintragen"); return; }
    setSubmitting(true);
    try {
      const issueName = selectedVariant
        ? `${sel.issue.name} · ${selectedVariant.name}`
        : sel.issue.name;
      await api.post("/repairs", {
        ...form, brand: sel.brand.name, model: sel.model, issue: issueName,
        estimated_price: effectivePrice || 0,
      });
      setDone(true);
      toast.success("Anfrage gesendet! Wir melden uns innerhalb 24h.");
    } catch (e) { toast.error("Fehler beim Senden"); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setSel({ brand: null, model: null, issue: null });
    setForm({ customer_name: "", phone: "", email: "", notes: "" });
    setDone(false); setQuote(null); setSelectedVariant(null); setStep(1);
  };

  return (
    <div id="configurator" className="bg-[#141417] border border-white/5 rounded-3xl overflow-hidden" data-testid="repair-configurator">
      <div className="px-8 pt-8 pb-4 flex items-center gap-3">
        {[1, 2, 3].map((n) => (
          <div key={n} className="flex items-center flex-1">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                step >= n ? "bg-[#DC1928] text-white" : "bg-white/5 text-zinc-500 border border-white/10"
              }`}
              data-testid={`step-indicator-${n}`}
            >
              {step > n ? <Check className="w-4 h-4" /> : n}
            </div>
            {n < 3 && <div className={`flex-1 h-px mx-3 ${step > n ? "bg-[#DC1928]" : "bg-white/10"}`}></div>}
          </div>
        ))}
      </div>

      {!quote && !done && (
        <div className="px-8 pt-2 pb-8">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Schritt {step} von 3</div>
          <h3 className="font-display text-2xl md:text-3xl font-semibold mt-1 text-white">
            {step === 1 && "Welche Marke?"}
            {step === 2 && "Welches Modell?"}
            {step === 3 && "Was ist das Problem?"}
          </h3>
        </div>
      )}

      {step === 1 && (
        <div className="px-8 pb-8 grid grid-cols-2 md:grid-cols-4 gap-3 fade-up">
          {brands.map((b) => (
            <button
              key={b.id}
              onClick={() => pick("brand", b, 2)}
              className={`group h-28 rounded-2xl border transition-all flex flex-col items-center justify-center gap-2 ${
                sel.brand?.id === b.id ? "border-[#DC1928] bg-[#DC1928]/10" : "border-white/10 hover:border-white/30 bg-[#1C1C20]"
              }`}
              data-testid={`brand-${b.id}`}
            >
              <div className="font-display font-bold text-2xl text-white tracking-tight">{BRAND_LABEL[b.id] || b.name}</div>
              <div className="text-xs text-zinc-500">{b.name}</div>
            </button>
          ))}
        </div>
      )}

      {step === 2 && (
        <div className="px-8 pb-8 fade-up">
          <div className="mb-4 flex items-center justify-between gap-3">
            <button onClick={() => setStep(1)} className="text-sm text-zinc-500 flex items-center gap-1 hover:text-white" data-testid="back-to-brand">
              <ChevronLeft className="w-4 h-4" /> {sel.brand?.name}
            </button>
          </div>

          {/* Schnellsuche */}
          <div className="relative mb-4" data-testid="model-search-wrap">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Modell suchen…  z.B. S22, Pixel 8, iPhone 13"
              className="w-full h-11 pl-10 pr-10 rounded-xl bg-[#0A0A0B] border border-white/10 text-white placeholder:text-zinc-600 focus:border-[#DC1928] focus:outline-none focus:ring-2 focus:ring-[#DC1928]/20 transition-all text-sm"
              autoFocus
              data-testid="model-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Suche löschen"
                data-testid="model-search-clear"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            )}
          </div>

          {/* Series tabs (hidden while user is searching) */}
          {MODEL_GROUPS[sel.brand?.id] && !search && (
            <div className="mb-4 flex flex-wrap gap-2" data-testid="model-group-tabs">
              {MODEL_GROUPS[sel.brand.id].map((g) => {
                const count = models.filter(g.match).length;
                if (count === 0) return null;
                const active = activeModelGroup === g.id;
                return (
                  <button
                    key={g.id}
                    onClick={() => setModelGroup(g.id)}
                    className={`px-4 h-9 rounded-full text-xs font-medium tracking-wide transition-all flex items-center gap-1.5 ${
                      active
                        ? "bg-[#DC1928] text-white shadow-[0_4px_14px_-4px_rgba(220,25,40,0.6)]"
                        : "bg-[#1C1C20] border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                    }`}
                    data-testid={`model-group-${g.id}`}
                  >
                    {g.name}
                    <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${active ? "bg-white/20" : "bg-white/5 text-zinc-500"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {(() => {
            // Filtering logic:
            //  1. If user typed a search, match across ALL models for the brand
            //     (case-insensitive substring on the model name).
            //  2. Otherwise, if the brand has model groups, restrict to the active group.
            //  3. Otherwise, show every model.
            const q = search.trim().toLowerCase();
            let filtered;
            if (q) {
              filtered = models.filter((m) => m.toLowerCase().includes(q));
            } else if (MODEL_GROUPS[sel.brand?.id]) {
              const grp = MODEL_GROUPS[sel.brand.id].find((g) => g.id === activeModelGroup);
              filtered = grp ? models.filter(grp.match) : models;
            } else {
              filtered = models;
            }

            if (q && filtered.length === 0) {
              return (
                <div className="py-10 text-center text-zinc-500" data-testid="model-search-empty">
                  <div className="font-display text-base">Kein Modell gefunden.</div>
                  <div className="text-xs mt-1">Versuchen Sie eine andere Schreibweise oder fragen Sie per WhatsApp an.</div>
                </div>
              );
            }

            return (
              <>
                {q && (
                  <div className="mb-3 text-[11px] text-zinc-500 uppercase tracking-[0.18em] font-semibold" data-testid="model-search-results-count">
                    {filtered.length} Treffer
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-1">
                  {filtered.map((m) => (
                    <button
                      key={m}
                      onClick={() => pick("model", m, 3)}
                      className={`text-left px-4 py-3 rounded-xl border transition-all ${
                        sel.model === m ? "border-[#DC1928] bg-[#DC1928]/10 text-white" : "border-white/10 hover:border-white/30 bg-[#1C1C20] text-zinc-300"
                      }`}
                      data-testid={`model-${m.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <div className="text-sm font-medium">{m}</div>
                    </button>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {step === 3 && !quote && (
        <div className="px-6 md:px-8 pb-8 fade-up">
          <div className="mb-5 flex items-center gap-2 text-sm text-zinc-500">
            <button onClick={() => setStep(2)} className="hover:text-white flex items-center gap-1" data-testid="back-to-model">
              <ChevronLeft className="w-4 h-4" /> {sel.brand?.name}
            </button>
            <span>·</span>
            <span className="text-white font-medium">{sel.model}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="issue-grid">
            {issues.map((iss) => {
              const Icon = ISSUE_ICONS[iss.id] || Wrench;
              return (
                <IssueTile
                  key={iss.id}
                  iss={iss}
                  Icon={Icon}
                  active={sel.issue?.id === iss.id}
                  onPick={() => pick("issue", iss, 3)}
                  brand={sel.brand?.id}
                  model={sel.model}
                />
              );
            })}
          </div>
        </div>
      )}

      {quote && !done && (
        <div className="relative bg-gradient-to-br from-[#1a0509] to-[#0A0A0B] p-8 md:p-10 border-t border-white/5 fade-up" data-testid="quote-result">
          <div className="absolute inset-0 bg-[#DC1928]/[0.07] pointer-events-none"></div>
          <div className="relative grid md:grid-cols-2 gap-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FF2D40] font-bold">Ihr Angebot</div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold mt-2 text-white">
                {sel.model}<br/>
                <span className="text-zinc-400 text-xl font-normal">{sel.issue.name} Reparatur</span>
              </h3>

              {/* Variant selector (only when variants are configured) */}
              {hasVariants && (
                <div className="mt-6 space-y-2" data-testid="variant-selector">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">Variante wählen</div>
                  <div className="grid grid-cols-2 gap-2">
                    {quote.variants.map((v) => {
                      const active = selectedVariant?.name === v.name;
                      return (
                        <button
                          key={v.name}
                          type="button"
                          onClick={() => setSelectedVariant(v)}
                          className={`px-3 py-2.5 rounded-xl border text-left transition-all ${
                            active
                              ? "border-[#DC1928] bg-[#DC1928]/15"
                              : "border-white/10 bg-[#141417] hover:border-white/30"
                          }`}
                          data-testid={`variant-${v.name.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <div className="text-sm font-medium text-white">{v.name}</div>
                          <div className={`text-xs font-semibold numeric mt-0.5 ${active ? "text-[#FF4F5E]" : "text-zinc-400"}`}>€{v.price}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-sm text-zinc-500">ab</span>
                <span className="font-display text-6xl font-bold text-white numeric" data-testid="quote-price">{effectivePrice}</span>
                <span className="text-3xl text-white">€</span>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm text-zinc-400">
                <Clock className="w-4 h-4" /> Reparaturdauer: {quote.duration}
              </div>
              <button onClick={reset} className="mt-6 text-sm text-zinc-500 hover:text-white underline underline-offset-2" data-testid="reset-configurator">
                Andere Reparatur konfigurieren
              </button>
            </div>

            <div className="bg-[#141417] rounded-2xl p-6 border border-white/10 space-y-3">
              <h4 className="font-display font-semibold text-lg text-white">Kontaktdaten</h4>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Name *</Label>
                  <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Max Mustermann" className="bg-[#0A0A0B] border-white/10 text-white" data-testid="repair-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Telefon *</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+43 660 1234567" className="bg-[#0A0A0B] border-white/10 text-white" data-testid="repair-phone-input" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">E-Mail (optional)</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@beispiel.at" className="bg-[#0A0A0B] border-white/10 text-white" data-testid="repair-email-input" />
                </div>
                <Button onClick={submit} disabled={submitting} className="w-full bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-11 mt-2 btn-lift glow-red-sm" data-testid="repair-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Angebot anfragen"}
                </Button>

                <div className="relative my-1">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/10" /></div>
                  <div className="relative flex justify-center text-[10px] uppercase tracking-[0.22em]"><span className="bg-[#141417] px-3 text-zinc-500">oder direkt</span></div>
                </div>

                {(() => {
                  const wa = whatsappLinks.repair({
                    brand: sel.brand?.name,
                    model: sel.model,
                    issue: selectedVariant
                      ? `${sel.issue?.name} · ${selectedVariant.name}`
                      : sel.issue?.name,
                    price: effectivePrice,
                    customerName: form.customer_name,
                  });
                  return (
                    <a
                      href={wa.href}
                      onClick={wa.onClick}
                      target="_blank"
                      rel="noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebd5b] text-white rounded-full h-11 btn-lift font-medium text-sm transition-colors"
                      data-testid="repair-whatsapp-btn"
                    >
                      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.498 14.382c-.301-.15-1.767-.872-2.04-.972-.273-.1-.472-.15-.672.15-.197.298-.771.97-.944 1.17-.174.2-.347.226-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.501.099-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.18 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.225-.57-.345m-5.446 7.443h-.016a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      Per WhatsApp anfragen
                    </a>
                  );
                })()}
                <p className="text-[10px] text-zinc-500 text-center">Unverbindlich · Antwort innerhalb 24h</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {done && (
        <div className="p-12 text-center fade-up" data-testid="repair-success">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4 border border-emerald-500/30">
            <Check className="w-8 h-8 text-emerald-400" />
          </div>
          <h3 className="font-display text-2xl font-semibold text-white">Anfrage gesendet!</h3>
          <p className="text-zinc-400 mt-2">Wir melden uns innerhalb von 24 Stunden bei dir.</p>
          <Button onClick={reset} variant="outline" className="mt-6 rounded-full bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white" data-testid="new-request-btn">
            Weitere Reparatur anfragen
          </Button>
        </div>
      )}
    </div>
  );
}


/* mcservice.at-style issue tile with inline "ab Preis" pre-loaded from /catalog/quote */
function IssueTile({ iss, Icon, active, onPick, brand, model }) {
  const [price, setPrice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (brand && model) {
      api
        .post("/catalog/quote", { brand, model, issue: iss.id })
        .then((r) => { if (!cancelled) setPrice(r.data?.price); })
        .catch(() => { if (!cancelled) setPrice(null); });
    }
    return () => { cancelled = true; };
  }, [brand, model, iss.id]);

  return (
    <button
      onClick={onPick}
      className={`text-left p-4 rounded-2xl border transition-all flex items-center gap-3 ${
        active
          ? "border-[#DC1928] bg-[#DC1928]/10"
          : "border-white/10 hover:border-white/30 bg-[#1C1C20] hover:bg-[#21212a]"
      }`}
      data-testid={`issue-${iss.id}`}
    >
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
          active ? "bg-[#DC1928]" : "bg-[#DC1928]/15"
        }`}
      >
        <Icon className={`w-5 h-5 ${active ? "text-white" : "text-[#FF4F5E]"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-white text-sm leading-tight truncate">{iss.name}</div>
        <div className="text-[11px] text-zinc-500 leading-tight mt-0.5 line-clamp-1">{iss.desc}</div>
      </div>
      <div className="text-right shrink-0 pl-1">
        {price === null && (
          <div className="text-[10px] uppercase tracking-wider text-zinc-600">Lädt…</div>
        )}
        {price === 0 && (
          <div className="text-[11px] text-emerald-400 font-semibold">Gratis</div>
        )}
        {price > 0 && (
          <>
            <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-semibold">ab</div>
            <div className="font-display font-bold text-white text-base numeric leading-none">€{price}</div>
          </>
        )}
      </div>
    </button>
  );
}
